// AgendaEntreprises.jsx — Agenda partagé des événements pro des membres
// Différent de /espace/evenements (réunions officielles asso). Ici : JPO, conférences, inaugurations
// organisés par les membres entreprises.
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { usePermissions } from '@/lib/usePermissions';
import { toast } from 'sonner';
import {
  CalendarDays, Plus, Loader2, Lock, MapPin, Globe, Star, Trash2, X, Tag, Users, ExternalLink, Pencil, Download,
} from 'lucide-react';
import ImageUploader from '@/components/ImageUploader';

const PUB_LABELS = { true: 'Tout public', false: 'Membres uniquement' };

export default function AgendaEntreprises() {
  const { user } = useAuth();
  const { can, loading: permsLoading } = usePermissions();
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ upcoming: '1', categorie: '', mine: '' });
  const [showCompose, setShowCompose] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api.get('/business-events/categories').then((r) => setCategories(r.data));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.upcoming) params.upcoming = '1';
      if (filter.categorie) params.categorie = filter.categorie;
      if (filter.mine) params.mine = '1';
      const { data } = await api.get('/business-events', { params });
      setEvents(data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (!permsLoading) load(); /* eslint-disable-next-line */ }, [filter, permsLoading]);

  if (permsLoading) return <div className="p-8" />;
  if (!can('annuaire', 'view')) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="bizevents-denied">
        <div className="card text-center py-12">
          <Lock className="w-8 h-8 mx-auto mb-3 text-[#CBD5E1]" />
          <h3 className="font-display text-lg font-bold text-[#0F172A] mb-2">Accès refusé</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto" data-testid="agenda-entreprises-page">
      <div className="mb-6 slide-up">
        <div className="label-uppercase mb-2" style={{ color: 'var(--theme-primary)' }}>Agenda partagé</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A]">Événements des entreprises</h1>
        <p className="text-sm text-[#475569] mt-2">Portes ouvertes, conférences, inaugurations… ce que proposent vos collègues du réseau.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <select
          value={filter.upcoming}
          onChange={(e) => setFilter({ ...filter, upcoming: e.target.value })}
          className="input-base sm:w-44"
          data-testid="filter-upcoming"
        >
          <option value="1">À venir</option>
          <option value="">Tout l'historique</option>
        </select>
        <select
          value={filter.categorie}
          onChange={(e) => setFilter({ ...filter, categorie: e.target.value })}
          className="input-base sm:w-56"
          data-testid="filter-categorie"
        >
          <option value="">Toutes catégories</option>
          {categories.map((c) => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
        </select>
        <select
          value={filter.mine}
          onChange={(e) => setFilter({ ...filter, mine: e.target.value })}
          className="input-base sm:w-44"
          data-testid="filter-mine"
        >
          <option value="">Tous les organisateurs</option>
          <option value="1">Mes événements</option>
        </select>
        <button onClick={() => { setEditing(null); setShowCompose(true); }} className="btn-primary flex items-center gap-1.5 whitespace-nowrap" data-testid="bizevent-new">
          <Plus className="w-4 h-4" /> Publier un événement
        </button>
      </div>

      {loading ? (
        <div className="py-12 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>
      ) : events.length === 0 ? (
        <div className="card text-center py-12 text-sm text-[#475569]" data-testid="bizevents-empty">
          <CalendarDays className="w-8 h-8 mx-auto mb-3 text-[#CBD5E1]" />
          Aucun événement publié pour ces critères.
          {filter.mine === '1' && <div className="mt-2">Publiez le 1er pour faire connaître votre activité !</div>}
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((e) => (
            <EventCard
              key={e.id} evt={e}
              categories={categories}
              isAuthor={e.author_id === user?.id}
              canModerate={can('annuaire', 'moderate')}
              onChanged={load}
              onEdit={() => { setEditing(e); setShowCompose(true); }}
            />
          ))}
        </div>
      )}

      {showCompose && (
        <ComposeEventModal
          evt={editing}
          categories={categories}
          onClose={() => { setShowCompose(false); setEditing(null); }}
          onSaved={() => { setShowCompose(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EventCard({ evt, categories, isAuthor, canModerate, onChanged, onEdit }) {
  const cat = categories.find((c) => c.value === evt.categorie);
  const date = new Date(evt.date_debut);
  const isPast = date < new Date();

  const toggleInterest = async () => {
    try {
      await api.post(`/business-events/${evt.id}/interest`);
      onChanged();
    } catch { toast.error('Erreur'); }
  };

  const remove = async () => {
    if (!window.confirm('Supprimer cet événement ?')) return;
    try { await api.delete(`/business-events/${evt.id}`); toast.success('Supprimé'); onChanged(); }
    catch { toast.error('Erreur'); }
  };

  const downloadIcs = async () => {
    try {
      const token = localStorage.getItem('assoperf_token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/business-events/${evt.id}/ics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${evt.titre.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 50)}.ics`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Erreur téléchargement'); }
  };

  return (
    <div className={`card flex flex-col sm:flex-row gap-3 sm:gap-4 ${isPast ? 'opacity-70' : ''}`} data-testid={`bizevent-${evt.id}`}>
      <div className="flex items-center sm:items-start gap-3 sm:gap-4 sm:shrink-0">
        <div className="w-16 h-16 rounded-xl text-white grid place-items-center flex-col leading-none shrink-0" style={{ background: 'var(--theme-primary)' }}>
          <div className="text-2xl font-display font-black">{date.getDate()}</div>
          <div className="text-[10px] font-bold uppercase">{date.toLocaleDateString('fr-FR', { month: 'short' })}</div>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {cat && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#0F172A] flex items-center gap-1">{cat.icon} {cat.label}</span>}
              {evt.public_ouvert && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Tout public</span>}
              {isPast && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-[#475569]">Passé</span>}
            </div>
            <h3 className="font-display font-bold text-[#0F172A] text-base sm:text-lg leading-tight">{evt.titre}</h3>
            <div className="text-xs text-[#475569] mt-1">Par <strong>{evt.author_nom}</strong> · {date.toLocaleString('fr-FR', { weekday: 'short', day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' })}</div>
          </div>
          {(isAuthor || canModerate) && (
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={onEdit} className="text-[#94A3B8] hover:text-[#0F172A] p-1" title="Modifier" data-testid={`bizevent-edit-${evt.id}`}><Pencil className="w-3.5 h-3.5" /></button>
              <button onClick={remove} className="text-[#94A3B8] hover:text-rose-600 p-1" title="Supprimer" data-testid={`bizevent-delete-${evt.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          )}
        </div>
        {evt.description && <p className="text-sm text-[#0F172A] mt-2 line-clamp-2 sm:line-clamp-3">{evt.description}</p>}
        {evt.image_url && (
          <img src={evt.image_url} alt="" className="mt-2 rounded-lg max-h-40 object-cover w-full sm:max-w-md bg-[#F1F5F9]" />
        )}
        <div className="flex items-center gap-3 mt-3 text-xs text-[#475569] flex-wrap">
          {evt.lieu && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {evt.lieu}</span>}
          {evt.site_web && (
            <a href={evt.site_web.startsWith('http') ? evt.site_web : `https://${evt.site_web}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline">
              <Globe className="w-3 h-3" /> Site
            </a>
          )}
          {evt.prix_indicatif !== null && evt.prix_indicatif !== undefined && (
            <span className="font-bold">{evt.prix_indicatif === 0 ? 'Gratuit' : `${evt.prix_indicatif} €`}</span>
          )}
          {!isPast && (
            <button
              onClick={toggleInterest}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold transition-colors ${
                evt.je_participe ? 'text-white' : 'bg-[#F1F5F9] text-[#0F172A] hover:bg-slate-200'
              }`}
              style={evt.je_participe ? { background: 'var(--theme-primary)' } : {}}
              data-testid={`bizevent-interest-${evt.id}`}
            >
              <Star className={`w-3.5 h-3.5 ${evt.je_participe ? 'fill-current' : ''}`} />
              {evt.je_participe ? 'Je participe' : 'Je m\'intéresse'}
            </button>
          )}
          <button
            onClick={downloadIcs}
            className="ml-auto flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold bg-[#F1F5F9] text-[#0F172A] hover:bg-slate-200"
            title="Ajouter à mon calendrier (.ics)"
            data-testid={`bizevent-ics-${evt.id}`}
          >
            <Download className="w-3.5 h-3.5" /> .ics
          </button>
        </div>
      </div>
    </div>
  );
}

function ComposeEventModal({ evt, categories, onClose, onSaved }) {
  const isEdit = !!evt;
  const initial = evt || {
    titre: '', description: '', categorie: 'autre',
    date_debut: '', date_fin: '', lieu: '', adresse: '', site_web: '',
    image_url: '', public_ouvert: true, prix_indicatif: '',
  };
  const [form, setForm] = useState({
    ...initial,
    date_debut: initial.date_debut ? new Date(initial.date_debut).toISOString().slice(0, 16) : '',
    date_fin: initial.date_fin ? new Date(initial.date_fin).toISOString().slice(0, 16) : '',
    prix_indicatif: initial.prix_indicatif !== null && initial.prix_indicatif !== undefined ? String(initial.prix_indicatif) : '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.titre.trim()) return toast.error('Titre requis');
    if (!form.date_debut) return toast.error('Date début requise');
    setSaving(true);
    try {
      const payload = {
        ...form,
        prix_indicatif: form.prix_indicatif === '' ? null : parseFloat(form.prix_indicatif),
      };
      if (!payload.date_fin) delete payload.date_fin;
      if (isEdit) await api.put(`/business-events/${evt.id}`, payload);
      else await api.post('/business-events', payload);
      toast.success(isEdit ? 'Événement modifié' : 'Événement publié');
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-sm grid place-items-start justify-center p-3 sm:p-6 fade-in overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} className="card max-w-lg w-full my-6" onClick={(e) => e.stopPropagation()} data-testid="bizevent-compose">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold text-[#0F172A]">{isEdit ? 'Modifier l\'événement' : 'Publier un événement'}</h3>
          <button type="button" onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label-uppercase block mb-1.5">Titre *</label>
            <input required type="text" value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} className="input-base" placeholder="Ex: Journée portes ouvertes — Atelier Boulanger" data-testid="bizevent-titre" maxLength={200} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-uppercase block mb-1.5">Catégorie</label>
              <select value={form.categorie} onChange={(e) => setForm({ ...form, categorie: e.target.value })} className="input-base" data-testid="bizevent-categorie">
                {categories.map((c) => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-uppercase block mb-1.5">Public</label>
              <select value={form.public_ouvert ? '1' : '0'} onChange={(e) => setForm({ ...form, public_ouvert: e.target.value === '1' })} className="input-base">
                <option value="1">Tout public</option>
                <option value="0">Membres uniquement</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-uppercase block mb-1.5">Début *</label>
              <input required type="datetime-local" value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} className="input-base" data-testid="bizevent-date-debut" />
            </div>
            <div>
              <label className="label-uppercase block mb-1.5">Fin (optionnel)</label>
              <input type="datetime-local" value={form.date_fin} onChange={(e) => setForm({ ...form, date_fin: e.target.value })} className="input-base" />
            </div>
          </div>
          <div>
            <label className="label-uppercase block mb-1.5">Description</label>
            <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-base" maxLength={3000} placeholder="Programme, intervenants, conditions..." data-testid="bizevent-description" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-uppercase block mb-1.5">Lieu</label>
              <input type="text" value={form.lieu} onChange={(e) => setForm({ ...form, lieu: e.target.value })} className="input-base" placeholder="Ex: 14 rue Mercière" />
            </div>
            <div>
              <label className="label-uppercase block mb-1.5">Prix (€ optionnel)</label>
              <input type="number" step="0.01" min="0" value={form.prix_indicatif} onChange={(e) => setForm({ ...form, prix_indicatif: e.target.value })} className="input-base" placeholder="0 = gratuit" />
            </div>
          </div>
          <div>
            <label className="label-uppercase block mb-1.5">Site web (lien d'inscription)</label>
            <input type="text" value={form.site_web} onChange={(e) => setForm({ ...form, site_web: e.target.value })} className="input-base" placeholder="https://..." />
          </div>
          <ImageUploader
            value={form.image_url}
            onChange={(url) => setForm({ ...form, image_url: url })}
            label="Image (optionnel)"
            testid="bizevent-image-uploader"
            hint="PNG/JPG/WEBP · 5 Mo max · Format paysage recommandé"
            aspect="16/9"
          />
        </div>
        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-[#F1F5F9]">
          <button type="button" onClick={onClose} className="btn-secondary">Annuler</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="bizevent-submit">{saving ? 'Publication…' : (isEdit ? 'Mettre à jour' : 'Publier')}</button>
        </div>
      </form>
    </div>
  );
}
